import React from "react";
import { Tooltip } from "@material-ui/core";
import { useCurrentPageInfo } from "@hooks";
import { ServiceHelper } from "@modules";
import { nsiService } from "@services";
import { DataTable } from "../Common";
import { DataTableColumn } from "../Common/DataTable/DataTable";
import { useSnackbar } from "notistack";
import { CsvLoadStatus } from "@interfaces/CsvLoadStatus";
import { parseCsvImportError } from "@modules/CsvImportErrorParser";

/** Компактный рендер "Сообщения": ошибка CSV-импорта в этой таблице — сырой
 * текст Postgres-исключения с полным дампом строки внутри, в ячейке грида
 * это нечитаемая простыня. Показываем короткую человекочитаемую часть
 * (summary), полный разбор — во всплывающей подсказке. Имена полей core
 * кладёт в сам текст с 30.09.2026 (см. CsvImportErrorParser.ts) — здесь,
 * в отличие от диалога загрузки, схему по /nsi_meta/type_schema для старого
 * формата не подтягиваем (список может быть длинным, N лишних запросов). */
const renderMessage = (rowData: CsvLoadStatus) => {
    const message = rowData.message;
    const parsed = parseCsvImportError(message);
    if (!parsed) return message;

    const details =
        parsed.kind === "alias_not_found"
            ? `Поле «${parsed.attributeName ?? `#${parsed.atypeId}`}»: значение «${parsed.alias}» не найдено`
            : parsed.attrs
                  .map((attr) => `${attr.attributeName ?? `#${attr.atypeId}`}: ${JSON.stringify(attr.value)}`)
                  .join("\n");

    return (
        <Tooltip title={<span style={{ whiteSpace: "pre-line" }}>{details}</span>}>
            <span>{parsed.summary}</span>
        </Tooltip>
    );
};

const CsvLoadStatusesTable = () => {
    const { enqueueSnackbar } = useSnackbar();
    const { permission, param } = useCurrentPageInfo();

    const handleDeleteClick = async (rowData: CsvLoadStatus | CsvLoadStatus[]) => {
        try {
            const ids = ServiceHelper.getIdsListFromRowDataObject(rowData);
            await nsiService.removeCsvLoadStatus(ids);
            enqueueSnackbar("Запись о загрузке успешно удалена", {
                variant: "success",
            });
        } catch (err) {
            const message = err.response.data.error_tooltip;
            enqueueSnackbar(message || "Произошла ошибка при удалении записи", {
                variant: "error",
            });
        }
    };

    const columns: DataTableColumn<CsvLoadStatus>[] = [
        {
            title: "#",
            field: "id",
            defaultSort: "desc",
            width: 100,
        },
        {
            title: "Название файла",
            field: "file_name",
            width: "auto",
        },
        {
            title: "Статус",
            field: "status",
            type: "boolean",
            width: "auto",
        },
        {
            title: "Сообщение",
            field: "message",
            width: "auto",
            render: renderMessage,
        },
        {
            title: "Дата загрузки",
            field: "loaded_at",
            filtering: false,
            type: "datetime",
            width: "auto",
        },
    ];

    return (
        <DataTable
            fetchFn={(params) =>
                nsiService.getCsvLoadStatuses({ ...params, entity_type_id: param })
            }
            columns={columns}
            displayField="file_name"
            permission={permission}
            onDeleteClick={handleDeleteClick}
            onMassDeleteClick={handleDeleteClick}
            filtering
            selection
            sorting
            search
        />
    );
};

export default CsvLoadStatusesTable;
